import React, { FC, useState } from 'react';
import { Titled } from 'react-titled';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { NavLink, useNavigate } from 'react-router-dom';
import { Box, Flex, Button, useModal } from '@resideo/blueprint-react';
import AppLayout from 'components/common/AppLayout';
import AccountTypeButton from 'components/SignUp/SelectAccountTypeButton';
import { AccountType } from 'types/types';
import NoticeLayoutRedesign from 'components/common/NoticeLayoutRedesign';
import NoticeLayoutFooter from 'components/common/NoticeLayoutFooter';
import useReturnToUrl from 'hooks/useReturnToUrl';
import { ADD_DISTRIBUTOR_OPTION_USER_TYPE } from '../../config/flags';
import { SHOPIFY_STORE_LOGIN_URL } from '../../config';
import { PersonalAccountModal, BusinessAccountModal } from './AccountTypeModal';

const AccountTypesContainer = styled(Flex)`
  justify-content: space-between;
  align-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.large}) {
    flex-direction: column;
  }
`;

const StyledAccountTypeButton = styled(AccountTypeButton)`
  margin-left: 35px;
  margin-bottom: 10px;

  &:last-child {
    margin-right: 35px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.large}) {
    margin-left: 5px;
    margin-right: 5px;

    &:last-child {
      margin-right: 5px;
    }
  }
`;

const NextButton = styled(Button)`
  width: 325px;

  @media (max-width: ${({ theme }) => theme.breakpoints.large}) {
    width: 262px;
  }
`;

const userTypes = {
  pro: 'partner',
  distributor: 'distributor',
  developer: 'developer',
  personal: 'consumer',
};

const SelectAccountType: FC = (): JSX.Element => {
  const { t } = useTranslation();
  const [accountType, setAccountType] = useState<AccountType>();
  const [modalAccountType, setModalAccountType] = useState<
    'personal' | 'business' | null
  >(null);
  const navigate = useNavigate();
  useReturnToUrl();
  const {
    Modal: AccountModal,
    openModal: openAccountModal,
    closeModal: closeAccountModal,
  } = useModal({
    newDesign: true,
  });

  const handleNextClick = () => {
    if (!accountType) return;

    // Only allow navigation for distributor when feature flag is ON
    if (accountType === 'distributor') {
      navigate(`/sign-up?userType=${userTypes[accountType]}`);
    } else if (accountType === 'personal') {
      setModalAccountType('personal');
      openAccountModal();
    } else if (accountType === 'pro') {
      setModalAccountType('business');
      openAccountModal();
    }
  };

  const handleCloseModal = () => {
    setModalAccountType(null);
    closeAccountModal();
  };

  const urlParams = new URLSearchParams(window.location.search);
  const isShopifyWorkflow = urlParams.get('userType') === 'distributor';

  const shopifyFooterBlocks =
    ADD_DISTRIBUTOR_OPTION_USER_TYPE &&
    isShopifyWorkflow &&
    SHOPIFY_STORE_LOGIN_URL
      ? () => [
          <NoticeLayoutFooter.Text key='sign-in'>
            {t('signUp.company.alreadyHaveAccount')}&nbsp;
            <NoticeLayoutFooter.HtmlLink href={SHOPIFY_STORE_LOGIN_URL}>
              {t('signUp.backToSignIn')}
            </NoticeLayoutFooter.HtmlLink>
          </NoticeLayoutFooter.Text>,
        ]
      : undefined;

  return (
    <Titled title={title => `${t('getStarted.pageTitle')} - ${title}`}>
      <AppLayout
        showHeader={false}
        showLeftNav={false}
        showGreyPanel={true}
        showFooter={false}>
        <NoticeLayoutRedesign
          title={t('getStarted.header')}
          customFooterBlocks={shopifyFooterBlocks}>
          <>
            <AccountTypesContainer marginBottom={['small', 'medium']}>
              <StyledAccountTypeButton
                accountType={'pro'}
                active={accountType === 'pro'}
                onChange={() => setAccountType('pro')}
              />
              <StyledAccountTypeButton
                accountType={'personal'}
                active={accountType === 'personal'}
                onChange={() => setAccountType('personal')}
              />
              {ADD_DISTRIBUTOR_OPTION_USER_TYPE && isShopifyWorkflow && (
                <StyledAccountTypeButton
                  accountType={'distributor'}
                  active={accountType === 'distributor'}
                  onChange={() => setAccountType('distributor')}
                />
              )}
            </AccountTypesContainer>
            <Box marginBottom={['medium', 'xLarge']}>
              {ADD_DISTRIBUTOR_OPTION_USER_TYPE && isShopifyWorkflow ? (
                // When feature flag is ON: Use onClick handler with conditional logic
                <NextButton
                  className='common-button primary'
                  variant='primary'
                  disabled={!accountType}
                  data-test-get-started-next-btn
                  onClick={handleNextClick}>
                  {t('common.next')}
                </NextButton>
              ) : (
                // When feature flag is OFF: Use direct navigation for all types
                <>
                  {accountType === 'pro' || accountType === 'personal' ? (
                    <NavLink to={`/sign-up?userType=${userTypes[accountType]}`}>
                      <NextButton
                        className='common-button primary'
                        variant='primary'
                        disabled={false}
                        data-test-get-started-next-btn>
                        {t('common.next')}
                      </NextButton>
                    </NavLink>
                  ) : (
                    <NextButton
                      className='common-button primary'
                      variant='primary'
                      disabled={true}
                      data-test-get-started-next-btn>
                      {t('common.next')}
                    </NextButton>
                  )}
                </>
              )}
            </Box>
            {ADD_DISTRIBUTOR_OPTION_USER_TYPE &&
              isShopifyWorkflow &&
              modalAccountType && (
                <AccountModal
                  title={
                    modalAccountType === 'personal'
                      ? t('user.signUp.accountTypeModal.personalAccount.title')
                      : t('user.signUp.accountTypeModal.businessAccount.title')
                  }>
                  {modalAccountType === 'personal' && (
                    <PersonalAccountModal onClose={handleCloseModal} />
                  )}
                  {modalAccountType === 'business' && (
                    <BusinessAccountModal onClose={handleCloseModal} />
                  )}
                </AccountModal>
              )}
          </>
        </NoticeLayoutRedesign>
      </AppLayout>
    </Titled>
  );
};

export default SelectAccountType;
